<template>
  <div class="json-formatter-container">
    <!-- Header do componente -->
    <div class="json-header" v-if="showHeader">
      <div class="json-title">
        <div class="file-icon"></div>
        <span>{{ title }}</span>
      </div>
      <div class="json-actions">
        <button 
          v-if="showCopyButton" 
          class="copy-button" 
          @click="copyToClipboard"
          :class="{ 'copied': isCopied }"
        >
          {{ isCopied ? '✅ Copiado!' : '📋 Copiar' }}
        </button>
        <button 
          v-if="showCollapseButton" 
          class="collapse-button" 
          @click="toggleCollapse"
        >
          {{ isCollapsed ? '📂 Expandir' : '📁 Colapsar' }}
        </button>
      </div>
    </div>

    <!-- Conteúdo JSON -->
    <div 
      class="json-content" 
      :class="[
        `theme-${theme}`, 
        { 'collapsed': isCollapsed },
        { 'with-line-numbers': showLineNumbers }
      ]"
      ref="jsonContent"
    >
      <div v-if="showLineNumbers" class="line-numbers">
        <div 
          v-for="(line, index) in formattedLines" 
          :key="index"
          class="line-number"
        >
          {{ index + 1 }}
        </div>
      </div>
      
      <pre class="json-code"><code v-html="highlightedJson"></code></pre>
    </div>

    <!-- Rodapé com estatísticas -->
    <div class="json-footer" v-if="showStats && !isCollapsed">
      <div class="stats">
        <span class="stat-item">📏 {{ stats.lines }} linhas</span>
        <span class="stat-item">📝 {{ stats.characters }} chars</span>
        <span class="stat-item">📦 {{ stats.size }} bytes</span>
        <span class="stat-item">🗂️ {{ stats.objects }} objetos</span>
        <span class="stat-item">📋 {{ stats.arrays }} arrays</span>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="isLoading" class="json-loading">
      <div class="loading-spinner"></div>
      <span>Formatando JSON...</span>
    </div>

    <!-- Error state -->
    <div v-if="hasError" class="json-error">
      <div class="error-icon">⚠️</div>
      <div class="error-message">
        <strong>Erro ao processar objeto:</strong>
        <pre>{{ errorMessage }}</pre>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'JsonFormatter',
  
  props: {
    // Objeto JavaScript a ser formatado
    data: {
      type: [Object, Array, String, Number, Boolean],
      default: () => ({})
    },
    
    // Tema visual
    theme: {
      type: String,
      default: 'dark',
      validator: (value) => ['dark', 'light', 'github'].includes(value)
    },
    
    // Título do componente
    title: {
      type: String,
      default: 'formatted.json'
    },
    
    // Indentação (número de espaços)
    indent: {
      type: Number,
      default: 2
    },
    
    // Mostrar header
    showHeader: {
      type: Boolean,
      default: true
    },
    
    // Mostrar botão de copiar
    showCopyButton: {
      type: Boolean,
      default: true
    },
    
    // Mostrar botão de colapsar
    showCollapseButton: {
      type: Boolean,
      default: true
    },
    
    // Mostrar números das linhas
    showLineNumbers: {
      type: Boolean,
      default: false
    },
    
    // Mostrar estatísticas
    showStats: {
      type: Boolean,
      default: true
    },
    
    // Altura máxima do container
    maxHeight: {
      type: String,
      default: '500px'
    },
    
    // Expandir/colapsar inicial
    initiallyCollapsed: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      isCollapsed: this.initiallyCollapsed,
      isCopied: false,
      isLoading: false,
      hasError: false,
      errorMessage: '',
      formattedJson: '',
      highlightedJson: '',
      formattedLines: []
    }
  },
  
  computed: {
    stats() {
      if (!this.formattedJson) return { lines: 0, characters: 0, size: 0, objects: 0, arrays: 0 };
      
      const lines = this.formattedJson.split('\n').length;
      const characters = this.formattedJson.length;
      const size = new Blob([this.formattedJson]).size;
      
      let objects = 0;
      let arrays = 0;
      
      const countTypes = (obj) => {
        if (Array.isArray(obj)) {
          arrays++;
          obj.forEach(countTypes);
        } else if (obj !== null && typeof obj === 'object') {
          objects++;
          Object.values(obj).forEach(countTypes);
        }
      };
      
      try {
        countTypes(this.data);
      } catch (e) {
        // Ignorar erros na contagem
      }
      
      return { lines, characters, size, objects, arrays };
    }
  },
  
  watch: {
    data: {
      handler() {
        this.formatJson();
      },
      deep: true,
      immediate: true
    },
    
    indent() {
      this.formatJson();
    },
    
    theme() {
      this.formatJson();
    }
  },
  
  methods: {
    async formatJson() {
      this.isLoading = true;
      this.hasError = false;
      this.errorMessage = '';
      
      try {
        // Simular delay para objetos grandes
        await this.$nextTick();
        
        if (this.data === null || this.data === undefined) {
          this.formattedJson = 'null';
        } else {
          this.formattedJson = JSON.stringify(this.data, null, this.indent);
        }
        
        this.highlightedJson = this.highlightJSON(this.formattedJson);
        this.formattedLines = this.formattedJson.split('\n');
        
        this.isLoading = false;
        
        // Emitir evento quando formatação for concluída
        this.$emit('formatted', {
          original: this.data,
          formatted: this.formattedJson,
          stats: this.stats
        });
        
      } catch (error) {
        this.hasError = true;
        this.errorMessage = error.message;
        this.isLoading = false;
        
        this.$emit('error', error);
      }
    },
    
    highlightJSON(jsonString) {
      return jsonString
        .replace(/(".*?")(\s*:)/g, '<span class="json-key">$1</span><span class="json-comma">$2</span>')
        .replace(/:\s*(".*?")/g, ': <span class="json-string">$1</span>')
        .replace(/:\s*(-?\d+\.?\d*)/g, ': <span class="json-number">$1</span>')
        .replace(/:\s*(true|false)/g, ': <span class="json-boolean">$1</span>')
        .replace(/:\s*(null)/g, ': <span class="json-null">$1</span>')
        .replace(/([{}])/g, '<span class="json-bracket">$1</span>')
        .replace(/(\[|\])/g, '<span class="json-bracket">$1</span>')
        .replace(/(,)(?=\s*[}\]])/g, '<span class="json-comma">$1</span>')
        .replace(/(,)(?=\s*")/g, '<span class="json-comma">$1</span>');
    },
    
    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.formattedJson);
        this.isCopied = true;
        
        setTimeout(() => {
          this.isCopied = false;
        }, 2000);
        
        this.$emit('copied', this.formattedJson);
        
      } catch (error) {
        console.error('Erro ao copiar:', error);
        this.$emit('copy-error', error);
        
        // Fallback para browsers mais antigos
        this.fallbackCopy();
      }
    },
    
    fallbackCopy() {
      const textArea = document.createElement('textarea');
      textArea.value = this.formattedJson;
      document.body.appendChild(textArea);
      textArea.select();
      
      try {
        document.execCommand('copy');
        this.isCopied = true;
        setTimeout(() => {
          this.isCopied = false;
        }, 2000);
      } catch (error) {
        console.error('Fallback copy failed:', error);
      }
      
      document.body.removeChild(textArea);
    },
    
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
      this.$emit('toggle-collapse', this.isCollapsed);
    },
    
    // Método público para expandir
    expand() {
      this.isCollapsed = false;
    },
    
    // Método público para colapsar
    collapse() {
      this.isCollapsed = true;
    },
    
    // Método público para reprocessar
    refresh() {
      this.formatJson();
    }
  }
}
</script>

<style scoped>
.json-formatter-container {
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', 'Monaco', 'Courier New', monospace;
}

/* Header */
.json-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: #2d2d2d;
  border-bottom: 1px solid #404040;
}

.json-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #61dafb;
  font-weight: bold;
  font-size: 14px;
}

.file-icon {
  width: 16px;
  height: 16px;
  background: #f39c12;
  border-radius: 2px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  color: white;
}

.file-icon::after {
  content: '{}';
}

.json-actions {
  display: flex;
  gap: 8px;
}

.copy-button, .collapse-button {
  background: #007acc;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.copy-button:hover, .collapse-button:hover {
  background: #005a9e;
  transform: translateY(-1px);
}

.copy-button.copied {
  background: #28a745;
}

.collapse-button {
  background: #6c757d;
}

.collapse-button:hover {
  background: #545b62;
}

/* Conteúdo */
.json-content {
  position: relative;
  max-height: v-bind(maxHeight);
  overflow: auto;
  background: #1e1e1e;
  transition: all 0.3s ease;
}

.json-content.collapsed {
  max-height: 0;
  overflow: hidden;
}

.json-content::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.json-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.json-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
}

.json-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

.json-content.with-line-numbers {
  padding-left: 60px;
}

.line-numbers {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 50px;
  background: rgba(0, 0, 0, 0.3);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  padding: 20px 5px;
  font-size: 11px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.4);
  text-align: right;
  user-select: none;
  overflow: hidden;
}

.json-code {
  max-width: 1024px;
  text-align: left;
  padding: 20px;
  margin: 0;
  font-size: 13px;
  line-height: 1.7;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-weight: 400;
  letter-spacing: 0.3px;
  color: #d4d4d4;
}

/* Syntax Highlighting - Tema Escuro */
.theme-dark {
  background: #1e1e1e;
}

.theme-dark .json-key {
  color: #92c5f7;
  font-weight: 600;
}

.theme-dark .json-string {
  color: #9ccc65;
  word-break: break-all;
}

.theme-dark .json-number {
  color: #ff9500;
  font-weight: 600;
}

.theme-dark .json-boolean {
  color: #c792ea;
  font-weight: 600;
}

.theme-dark .json-null {
  color: #ff5370;
  font-weight: 600;
}

.theme-dark .json-bracket {
  color: #89ddff;
  font-weight: bold;
  font-size: 14px;
}

.theme-dark .json-comma {
  color: #89ddff;
  font-weight: bold;
}

/* Tema Claro */
.theme-light {
  background: #ffffff;
}

.theme-light .json-header {
  background: #f6f8fa;
  border-bottom: 1px solid #e1e4e8;
}

.theme-light .json-title {
  color: #24292e;
}

.theme-light .json-code {
  color: #24292e;
}

.theme-light .json-key {
  color: #005cc5;
  font-weight: 600;
}

.theme-light .json-string {
  color: #032f62;
}

.theme-light .json-number {
  color: #005cc5;
  font-weight: 600;
}

.theme-light .json-boolean {
  color: #d73a49;
  font-weight: 600;
}

.theme-light .json-null {
  color: #6f42c1;
  font-weight: 600;
}

.theme-light .json-bracket {
  color: #24292e;
  font-weight: bold;
}

.theme-light .json-comma {
  color: #24292e;
}

/* Tema GitHub */
.theme-github {
  background: #0d1117;
}

.theme-github .json-header {
  background: #161b22;
  border-bottom: 1px solid #30363d;
}

.theme-github .json-title {
  color: #f0f6fc;
}

.theme-github .json-code {
  color: #e6edf3;
}

.theme-github .json-key {
  color: #79c0ff;
  font-weight: 600;
}

.theme-github .json-string {
  color: #a5d6ff;
}

.theme-github .json-number {
  color: #79c0ff;
  font-weight: 600;
}

.theme-github .json-boolean {
  color: #ff7b72;
  font-weight: 600;
}

.theme-github .json-null {
  color: #d2a8ff;
  font-weight: 600;
}

.theme-github .json-bracket {
  color: #f0f6fc;
  font-weight: bold;
}

.theme-github .json-comma {
  color: #e6edf3;
}

/* Footer */
.json-footer {
  padding: 12px 20px;
  background: rgba(0, 0, 0, 0.1);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.stats {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.stat-item {
  background: rgba(255, 255, 255, 0.1);
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
}

/* Loading */
.json-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: #1e1e1e;
  color: #d4d4d4;
  gap: 12px;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top: 2px solid #007acc;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error */
.json-error {
  display: flex;
  align-items: flex-start;
  padding: 20px;
  background: #2d1618;
  color: #ff6b6b;
  border: 1px solid #ff4757;
  border-radius: 6px;
  margin: 20px;
  gap: 12px;
}

.error-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.error-message {
  flex: 1;
}

.error-message pre {
  margin-top: 8px;
  padding: 8px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
  font-size: 12px;
  white-space: pre-wrap;
}

/* Responsividade */
@media (max-width: 768px) {
  .json-header {
    flex-direction: column;
    gap: 8px;
    align-items: stretch;
  }
  
  .json-actions {
    justify-content: center;
  }
  
  .json-code {
    padding: 15px;
    font-size: 12px;
  }
  
  .stats {
    justify-content: center;
  }
}
</style>